/**
 * The agent's copy of the bar (RFC 0297 §4d, S4).
 *
 * THE SAME PROJECTION, A SECOND RENDERER. `projectSessionHeldDocuments` answers
 * what the session holds; the bar above the composer draws it as rows and this
 * draws it as text. A field that appears in one and not the other is a bug in a
 * renderer, not a difference in what is known, which is the invariant §4d
 * exists to state. That is why the formatting lives here rather than in the
 * daemon: a second implementation beside the first is how the two surfaces
 * start disagreeing about what an agent is working from.
 *
 * THE ID IS PRINTED IN FULL, which is the one place this departs from the
 * RFC's sketch. The sketch abbreviates it to fit a page. An abbreviated id is
 * an id the agent cannot pass to `read_document`, so the block would tell it
 * something is stale and give it no way to fix that, which is worse than saying
 * nothing.
 *
 * THE SELECTION SITS UNDER ITS DOCUMENT, never in a list of its own (§4e). It
 * is a part of something already held, and a second list would be a list of
 * things pointing at nothing. It reaches this block off the message that
 * stamped it rather than off window state, which is what keeps a per-click
 * write off the log.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import type { StampedSelection } from '../log'
import { THREAD_KEY } from '../log/thread-key'
import { countedComments } from '../model/comments/store'
import type { HeldDocument } from '../model/session/held-documents'
import {
  projectSessionHeldDocuments,
  projectSessionHoldings,
  projectSessionSelection,
} from '../model/session/reads'
/**
 * How many rows the agent is given.
 *
 * Deliberately not the bar's five (§7 flags the divergence). A person scans a
 * bar and needs it to stay a bar; a model reads a list and twenty lines cost it
 * nothing. Both render the same rows in the same order, which is the part §4d
 * requires them to agree on.
 */
export const PROMPT_ROWS = 20

/** "4m", "2h", "3d". Short, because it sits in a row that is mostly title. */
export function ago(at: number, now: number): string {
  // Clamped at zero. Two peers do not agree about the clock, and a holding
  // touched "-3s ago" reads as a bug in the data rather than in the clock.
  const s = Math.max(0, Math.round((now - at) / 1000))
  if (s < 60) return `${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h`
  return `${Math.round(h / 24)}d`
}

/** One holding, as one line. Tab-free, so it survives any prompt assembler. */
function line(row: HeldDocument, now: number): string {
  const parts = [row.title, `(${row.documentId})`]
  if (row.path && row.path !== row.title) parts.push(row.path)
  parts.push(`${row.lastKind} ${ago(row.lastAt, now)} ago`)

  // MISSING OUTRANKS CHANGED. A document that has left the catalog cannot be
  // read, so telling the agent to re-read it would send it at a tool call that
  // can only fail.
  if (row.missing) parts.push('MISSING from this project')
  else if (row.changed) parts.push(`CHANGED ${ago(row.updatedAt, now)} ago`)
  else parts.push('unchanged')

  if (row.newComments > 0) {
    parts.push(
      `${row.newComments} new comment${row.newComments === 1 ? '' : 's'}`,
    )
  }
  return `  ${parts.join('  ')}`
}

/**
 * The `<holdings>` block, or '' when this session holds nothing.
 *
 * '' RATHER THAN AN EMPTY BLOCK, matching `formatAvailableSkills`: a host
 * appends unconditionally, and a session that has touched no documents adds
 * nothing to its prompt rather than a heading with nothing under it.
 *
 * Pure, over rows a caller has already projected, so the wording can be tested
 * against a handful of literals rather than against a repo.
 */
export function formatHoldings(
  rows: HeldDocument[],
  now: number,
  /** What the person last pointed at (§4e). Drawn under its own document, and
   *  dropped entirely when that document is not among the rows: a passage in
   *  something nobody is holding has nowhere to belong. */
  selection?: StampedSelection | null,
): string {
  if (rows.length === 0) return ''
  const shown = rows.slice(0, PROMPT_ROWS)
  const more =
    rows.length > shown.length
      ? [`  …and ${rows.length - shown.length} more.`]
      : []
  const body: string[] = []
  for (const r of shown) {
    body.push(line(r, now))
    if (selection && selection.documentId === r.documentId) {
      body.push(
        `    pointing at: ${selection.label}${
          selection.ref ? `  [${selection.ref}]` : ''
        }`,
      )
    }
  }
  return [
    '<holdings>',
    'Documents this session has in hand, most deliberately held first. What ' +
      'you have of each is what you read at the time given. Re-read one with ' +
      'read_document before answering about it if it is marked CHANGED, or if ' +
      'it has comments you have not seen; reading is also what clears those ' +
      'marks. Where a line says what the person is pointing at, that is what ' +
      '"this" means in their message.',
    ...body,
    ...more,
    '</holdings>',
  ].join('\n')
}

/** An app open beside the conversation, with its skill's text (RFC 0334 3d). */
export interface HeldAppSkill {
  skillId: string
  title: string
  documentId: string
  text: string
}

/**
 * The `<held_apps>` block, or '' when no app is open.
 *
 * FULL TEXT, NOT A NAME. A name is what failed before: the agent saw a skill id
 * beside Weekly, did not read it, and guessed what a week was. An app open in
 * the view is held the way a document is, so its instructions arrive with the
 * turn instead of behind a `read_app` and a `read_document` it may not make.
 */
export function formatHeldApps(apps: readonly HeldAppSkill[]): string {
  if (apps.length === 0) return ''
  return [
    '<held_apps>',
    'Apps open beside this conversation, each with its skill in full. When ' +
      'the person is working in one, follow its skill; you do not need to ' +
      'read_app or read_document it first.',
    ...apps.map(
      (a) =>
        `<app name="${escapeAttr(a.title)}" skill="${escapeAttr(a.skillId)}" ` +
        `document_id="${a.documentId}">\n${a.text}\n</app>`,
    ),
    '</held_apps>',
  ].join('\n')
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;')
}

/** How long resolving the two documents may hold up serving a turn. Matches
 *  `readDocBounded`, and for the same reason: this is on the critical path. */
const HOLDINGS_TIMEOUT_MS = 5000

/**
 * Why the block came back empty.
 *
 * `''` has five exits, and the caller can see none of them. Logging "holdings
 * empty" without this says only that something happened, which is where the
 * last round of this bug hid: a session whose log had never synced and a session
 * holding nothing produced the same empty string, and the only way to tell them
 * apart was to read the source.
 *
 * `unsynced` IS THE ONE THAT MATTERS. A session document this peer has never
 * seen answers with no thread at all, and guarding `topoOrder` against that made
 * it project as an empty list — correct, and it moved the ambiguity up a level
 * rather than removing it. The bit that separates a sync fault from an empty
 * hand is whether the thread was there to read, so that is the bit reported.
 */
export type HoldingsEmptyReason =
  | { kind: 'no-session' }
  | { kind: 'no-project' }
  | { kind: 'timeout'; timeoutMs: number }
  | { kind: 'error'; error: unknown }
  /** The session doc carries no thread: not synced to this peer. */
  | { kind: 'unsynced' }
  /** The thread is there and the hand is genuinely empty. */
  | { kind: 'empty'; entries: number }

/** Render a reason as one short clause, so every caller logs it the same way. */
export function describeHoldingsEmpty(reason: HoldingsEmptyReason): string {
  switch (reason.kind) {
    case 'no-session':
      return 'no session id'
    case 'no-project':
      return 'no project for this session'
    case 'timeout':
      return `documents did not resolve in ${reason.timeoutMs}ms`
    case 'error':
      return `read failed: ${(reason.error as Error)?.message ?? String(reason.error)}`
    case 'unsynced':
      return 'session log NOT SYNCED to this peer (no thread on the doc)'
    case 'empty':
      return `nothing held (${reason.entries} log entries)`
  }
}

/**
 * How many entries the session's thread has, or null when there is no thread.
 *
 * Null and zero are different answers and the whole point of this file's log
 * line is to keep them apart: null is a document that never arrived, zero is one
 * that arrived empty.
 */
function threadEntryCount(sessionDoc: unknown): number | null {
  if (!sessionDoc || typeof sessionDoc !== 'object') return null
  const thread = (sessionDoc as Record<string, unknown>)[THREAD_KEY] as
    | { entries?: Record<string, unknown> }
    | undefined
  if (!thread?.entries || typeof thread.entries !== 'object') return null
  return Object.keys(thread.entries).length
}

/** Hydrate one doc, or null. Never throws: see `readHoldingsPrompt`. */
async function snapshot(repo: Repo, id: string): Promise<unknown> {
  const handle = await repo.find(id as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  return handle.doc()
}

/**
 * The block, resolved from the repo — the serve-time helper a host calls.
 *
 * BEST-EFFORT AND BOUNDED, exactly as `readDocBounded` is. A document that
 * will not hydrate, or will not answer inside the timeout, costs the agent its
 * holdings block; a hang costs it the conversation. Returning '' is also the
 * honest answer in that case, because a block built from half the data would
 * report a session holding less than it does.
 */
export async function readHoldingsPrompt(
  repo: Repo,
  sessionId: string,
  projectId: string | null | undefined,
  opts: {
    now?: number
    timeoutMs?: number
    /**
     * Called with the reason whenever this returns ''. A SEAM, not a logger:
     * mod-core does not know where a host writes its diagnostics, and the host
     * cannot see which of the five exits fired. Best-effort — a reporter that
     * throws must not cost the turn its prompt.
     */
    onEmpty?: (reason: HoldingsEmptyReason) => void
  } = {},
): Promise<string> {
  const report = (reason: HoldingsEmptyReason): string => {
    try {
      opts.onEmpty?.(reason)
    } catch {
      // A diagnostic is never worth failing a turn for.
    }
    return ''
  }

  if (!sessionId) return report({ kind: 'no-session' })
  if (!projectId) return report({ kind: 'no-project' })
  const timeoutMs = opts.timeoutMs ?? HOLDINGS_TIMEOUT_MS
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const resolved = await Promise.race([
      Promise.all([snapshot(repo, sessionId), snapshot(repo, projectId)]),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs)
        // Never keep a process alive just to give up on a holdings block.
        ;(timer as { unref?: () => void }).unref?.()
      }),
    ])
    if (!resolved) return report({ kind: 'timeout', timeoutMs })
    const [sessionDoc, projectDoc] = resolved
    const held = projectSessionHoldings(sessionDoc).map((h) => h.documentId)
    const listed = await countedComments(repo, projectId, projectDoc, held)
    const block = formatHoldings(
      projectSessionHeldDocuments(sessionDoc, projectDoc, sessionId, listed),
      opts.now ?? Date.now(),
      projectSessionSelection(sessionDoc),
    )
    if (block) return block
    // Empty, and the two ways of being empty are not the same fault.
    const entries = threadEntryCount(sessionDoc)
    return report(
      entries === null ? { kind: 'unsynced' } : { kind: 'empty', entries },
    )
  } catch (error) {
    return report({ kind: 'error', error })
  } finally {
    if (timer) clearTimeout(timer)
  }
}
